// ASR client. localStorage 'ezshadowAsrEngine': 'worker' (default, Whisper) or 'moonshine' (in-browser).
// Never send the reference sentence as a Whisper prompt: it echoes it back and
// every score becomes ~100.

import * as asrMoonshine from './asrMoonshine'

export var ASR_ENGINE_WORKER = 'worker'
export var ASR_ENGINE_MOONSHINE = 'moonshine'
var ASR_ENGINE_KEY = 'ezshadowAsrEngine'

// A saved 'moonshine' on a device that can't run it (iPhone) reads as 'worker'.
export function getAsrEngine () {
  try {
    var saved = typeof localStorage !== 'undefined' && localStorage.getItem(ASR_ENGINE_KEY)
    if (saved === ASR_ENGINE_MOONSHINE && !asrMoonshine.isSupported()) return ASR_ENGINE_WORKER
    return saved || ASR_ENGINE_WORKER
  } catch (e) {
    return ASR_ENGINE_WORKER
  }
}

export const ASR_TIMEOUT_MS = 8000

export const ASR_RETRY_DELAY_MS = 500

// Transient only; the other codes fail the same way on retry.
const RETRYABLE_ASR_CODES = ['TIMEOUT', 'NETWORK', 'UNAVAILABLE']

function sleep (ms) {
  return new Promise(function (resolve) { setTimeout(resolve, ms) })
}

// Matches the Worker's guard; ~30 s of Opus is far under it.
export const MAX_CLIP_BYTES = 1024 * 1024

export function AsrError (code, message) {
  const err = new Error(message || code)
  err.name = 'AsrError'
  err.code = code // 'NETWORK' | 'TIMEOUT' | 'RATE_LIMITED' | 'TOO_LARGE' | 'UNAVAILABLE'
  return err
}

export function isConfigured () {
  return !!process.env.TRANSCRIPT_API_URL
}

async function transcribeOnceViaWorker (blob, lang, apiUrl) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = setTimeout(function () {
    if (controller) controller.abort()
  }, ASR_TIMEOUT_MS)

  let url = apiUrl + '/v1/asr'
  if (lang) url += '?lang=' + encodeURIComponent(lang)

  let response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'application/octet-stream' },
      body: blob,
      signal: controller ? controller.signal : undefined
    })
  } catch (e) {
    clearTimeout(timer)
    const aborted = e && (e.name === 'AbortError' || e.code === 20)
    throw AsrError(aborted ? 'TIMEOUT' : 'NETWORK', e && e.message)
  }
  clearTimeout(timer)

  if (!response.ok) {
    if (response.status === 429) throw AsrError('RATE_LIMITED', 'per-minute transcription limit reached')
    if (response.status === 413) throw AsrError('TOO_LARGE', 'recording rejected as too large')

    // Quota resets at midnight UTC, an outage is worth retrying now: different advice.
    let code = 'UNAVAILABLE'
    try {
      const body = await response.json()
      if (body && body.error === 'AI_QUOTA') code = 'QUOTA_EXHAUSTED'
    } catch (e) { /* non-JSON error body */ }
    throw AsrError(code, 'ASR API responded ' + response.status)
  }

  let body
  try {
    body = await response.json()
  } catch (e) {
    throw AsrError('UNAVAILABLE', 'ASR API returned a non-JSON body')
  }

  return normalizeTranscript(body && body.text)
}

/** @param {string} [lang] code only, never the reference sentence. Resolves '' when nothing intelligible; throws AsrError. */
export async function transcribe (blob, lang) {
  if (!blob || !blob.size) return ''

  if (getAsrEngine() === ASR_ENGINE_MOONSHINE) {
    try {
      return await asrMoonshine.transcribe(blob)
    } catch (e) {
      throw AsrError((e && e.code) || 'UNAVAILABLE', e && e.message)
    }
  }

  if (blob.size > MAX_CLIP_BYTES) {
    throw AsrError('TOO_LARGE', 'recording is ' + blob.size + ' bytes')
  }

  const apiUrl = process.env.TRANSCRIPT_API_URL
  if (!apiUrl) throw AsrError('UNAVAILABLE', 'TRANSCRIPT_API_URL is not configured')

  // BUGFIX-004: on failure the caller discards a real captured attempt, so
  // retry a transient error once.
  try {
    return await transcribeOnceViaWorker(blob, lang, apiUrl)
  } catch (e) {
    if (!e || RETRYABLE_ASR_CODES.indexOf(e.code) === -1) throw e
    await sleep(ASR_RETRY_DELAY_MS)
    return transcribeOnceViaWorker(blob, lang, apiUrl)
  }
}

// Strip Whisper's non-speech tags ("[BLANK_AUDIO]", "(silence)").
export function normalizeTranscript (text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .replace(/\[[^\]]*\]|\([^)]*\)|\*[^*]*\*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
